import React, { useState, useEffect } from "react";
import { Wallet, TrendingUp, IndianRupee, Search } from "lucide-react";
import axios from "axios";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
} from "recharts";
import { ORDER_API } from "../../../data/api"; // adjust path

const Earning = () => {
  const [search, setSearch] = useState("");
  const [summary, setSummary] = useState({
    totalEarnings: 0,
    thisMonth: 0,
    pendingPayout: 0,
  });
  const [monthlyEarnings, setMonthlyEarnings] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  const vendorId = localStorage.getItem("vendorId");

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        const res = await axios.get(`${ORDER_API.GET_ALL}/vendor/${vendorId}`);
        const orders = res.data;

        // Total Earnings
        const total = orders.reduce(
          (sum, order) => sum + parseFloat(order.finalAmount || 0),
          0
        );

        // This month earnings
        const currentMonth = new Date().getMonth();
        const thisMonthEarnings = orders
          .filter((order) => new Date(order.createdAt).getMonth() === currentMonth)
          .reduce((sum, order) => sum + parseFloat(order.finalAmount || 0), 0);

        // Pending payout (Pending orders)
        const pending = orders
          .filter((order) => order.orderStatus === "Pending")
          .reduce((sum, order) => sum + parseFloat(order.finalAmount || 0), 0);

        setSummary({
          totalEarnings: total,
          thisMonth: thisMonthEarnings,
          pendingPayout: pending,
        });

        // Monthly earnings
        const months = [
          "Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"
        ];
        const monthly = months.map((month, index) => {
          const monthTotal = orders
            .filter((order) => new Date(order.createdAt).getMonth() === index)
            .reduce((sum, order) => sum + parseFloat(order.finalAmount || 0), 0);
          return { month, amount: monthTotal };
        });
        setMonthlyEarnings(monthly);

        // Transactions (flatten items for transaction view)
        const txn = [];
        orders.forEach((order) => {
          order.items.forEach((item) => {
            txn.push({
              transactionId: order.orderId,
              customerName: order.userId,
              productName: item.product.productName,
              amount: item.totalPrice,
              type: "Credit",
              date: order.createdAt,
            });
          });
        });
        setTransactions(txn);
      } catch (err) {
        console.error("Error fetching orders:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchOrders();
  }, [vendorId]);

  const filteredTransactions = transactions.filter((t) =>
    (t.customerName + " " + t.productName)
      .toLowerCase()
      .includes(search.toLowerCase())
  );

  if (loading) return <p className="text-center p-6">Loading earnings...</p>;

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-6">Earnings Overview</h1>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {/* Total Earnings */}
        <div className="bg-white shadow rounded-xl p-4 flex items-center gap-4">
          <Wallet className="text-blue-600" size={40} />
          <div>
            <p className="text-gray-500">Total Earnings</p>
            <h2 className="text-2xl font-bold">₹{summary.totalEarnings}</h2>
          </div>
        </div>

        {/* This Month */}
        <div className="bg-white shadow rounded-xl p-4 flex items-center gap-4">
          <TrendingUp className="text-green-600" size={40} />
          <div>
            <p className="text-gray-500">This Month</p>
            <h2 className="text-2xl font-bold">₹{summary.thisMonth}</h2>
          </div>
        </div>

        {/* Pending Payout */}
        <div className="bg-white shadow rounded-xl p-4 flex items-center gap-4">
          <IndianRupee className="text-purple-600" size={40} />
          <div>
            <p className="text-gray-500">Pending Payout</p>
            {/* Agar payout 0 hai to empty dikhaye */}
            <h2 className="text-2xl font-bold">
              {summary.pendingPayout > 0 ? `₹${summary.pendingPayout}` : "-"}
            </h2>
          </div>
        </div>
      </div>

      {/* Monthly Earnings Chart */}
      <div className="bg-white shadow rounded-xl p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Monthly Earnings</h2>
        <div className="w-full h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={monthlyEarnings}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="month" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="amount" fill="#4f46e5" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Recent Transactions */}
      <div className="bg-white shadow rounded-xl p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-semibold">Recent Transactions</h2>
          <div className="flex items-center gap-2 bg-gray-100 p-2 rounded-xl">
            <Search size={18} className="text-gray-500" />
            <input
              type="text"
              placeholder="Search customer/product..."
              className="bg-transparent outline-none"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="text-left border-b">
                <th className="p-3">Transaction ID</th>
                <th className="p-3">Customer/Product</th>
                <th className="p-3">Amount</th>
                <th className="p-3">Type</th>
                <th className="p-3">Date</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransactions.length > 0 ? (
                filteredTransactions.map((txn, idx) => (
                  <tr key={idx} className="border-b hover:bg-gray-50">
                    <td className="p-3 font-medium">{txn.transactionId}</td>
                    <td className="p-3">{txn.customerName} / {txn.productName}</td>
                    <td className="p-3">₹{txn.amount}</td>
                    <td className="p-3">
                      <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-700">
                        {txn.type}
                      </span>
                    </td>
                    <td className="p-3">{new Date(txn.date).toLocaleDateString()}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="text-center text-gray-400 p-4">
                    No transactions available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Earning;
